import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField,} from '@mui/material';
import {TimePicker} from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {IEtkinlik} from '../../../constants/types/logbook';
import {CloudUpload} from '@mui/icons-material';

interface EtkinlikFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (etkinlik: IEtkinlik, file?: File) => void;
    initialData?: IEtkinlik | null;
}

const EtkinlikFormDialog = ({open, onClose, onSave, initialData}: EtkinlikFormDialogProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | undefined>();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [formData, setFormData] = useState<IEtkinlik>({
        isim: '',
        aciklama: '',
        katilim_ucreti: '',
        etkinlik_tarihi: '',
        etkinlik_baslangic_saati: '00:00:00',
        etkinlik_bitis_saati: '00:00:00',
        aktif: true
    });

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
            setPreviewUrl(initialData.etkinlik_gorsel || null);
        } else {
            setFormData({
                isim: '',
                aciklama: '',
                katilim_ucreti: '',
                etkinlik_tarihi: '',
                etkinlik_baslangic_saati: '00:00:00',
                etkinlik_bitis_saati: '00:00:00',
                aktif: true
            });
            setPreviewUrl(null);
        }
        setSelectedFile(undefined);
    }, [initialData, open]);

    const handleChange = (field: keyof IEtkinlik) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({...formData, [field]: event.target.value});
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = () => {
        onSave(formData, selectedFile);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{initialData ? 'Etkinlik Düzenle' : 'Yeni Etkinlik Ekle'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{mt: 1}}>
                    <Grid item xs={12} display="flex" flexDirection="column" alignItems="center">
                        {previewUrl && (
                            <Box
                                component="img"
                                src={previewUrl}
                                sx={{
                                    width: '100%',
                                    maxHeight: 200,
                                    objectFit: 'contain',
                                    mb: 2,
                                    borderRadius: 1,
                                    border: '1px solid #ddd'
                                }}
                            />
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            style={{display: 'none'}}
                            ref={fileInputRef}
                            onChange={handleFileChange}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<CloudUpload/>}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {selectedFile ? 'Görseli Değiştir' : 'Görsel Yükle'}
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Etkinlik İsmi"
                            value={formData.isim}
                            onChange={handleChange('isim')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            label="Açıklama"
                            value={formData.aciklama}
                            onChange={handleChange('aciklama')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Katılım Ücreti"
                            value={formData.katilim_ucreti}
                            onChange={handleChange('katilim_ucreti')}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TimePicker
                            label="Başlangıç Saati"
                            value={dayjs(formData.etkinlik_baslangic_saati, 'HH:mm:ss')}
                            onChange={(newValue) => {
                                setFormData({
                                    ...formData,
                                    etkinlik_baslangic_saati: newValue?.format('HH:mm:ss') ?? '00:00:00'
                                });
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TimePicker
                            label="Bitiş Saati"
                            value={dayjs(formData.etkinlik_bitis_saati, 'HH:mm:ss')}
                            onChange={(newValue) => {
                                setFormData({
                                    ...formData,
                                    etkinlik_bitis_saati: newValue?.format('HH:mm:ss') ?? '00:00:00'
                                });
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Vazgeç</Button>
                <Button onClick={handleSave} color="primary" variant="contained">Tamam</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EtkinlikFormDialog;
